import type { MaintenanceStatus } from '@research-assistant/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Clock, ExternalLink, Gamepad2, RefreshCw } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';

import { CtLogo } from '@/components/brand/index.js';
import { Button } from '@/components/ui/button.js';
import { safeHttpUrl } from '@/lib/safe-url.js';

/**
 * MaintenanceScreen — full-page downtime UI shown to non-admin users
 * when the bridge is in maintenance mode. Renders from `AppShell` (no
 * sidebar / no header).
 *
 * Visual language follows Linear / Vercel / Stripe status pages:
 *   - centered, brand mark + name big at the top
 *   - calm headline, one-line supportive copy
 *   - optional ETA pill
 *   - "Try again" button — refetches the maintenance status query in
 *     place (no `window.location.reload`) so the screen does NOT
 *     unmount, flash the chat, then come back. If status is still
 *     `enabled`, the screen stays put; if it flipped off, the app
 *     mounts cleanly without a flash of the maintenance UI.
 *   - "Play with Manu" — secondary CTA into the maintenance-screen
 *     mini-game. Standard outline button; the game itself owns its
 *     own brand visual.
 */
export function MaintenanceScreen({
  status,
}: {
  status: MaintenanceStatus;
}): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const channelUrl = safeHttpUrl(status.channelUrl);

  async function tryAgain(): Promise<void> {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ['maintenance-status'] });
    } finally {
      // Brief min-spin so the click feels acknowledged even on fast networks.
      setTimeout(() => setRefreshing(false), 300);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden bg-background px-6">
      <div className="flex w-full max-w-md flex-col items-center gap-8 text-center">
        <div className="flex items-center gap-3.5">
          <CtLogo
            variant="mark"
            tone="mono"
            className="size-11 text-[#18399b] [[data-theme='dark']_&]:text-white"
            aria-label="Customertimes"
          />
          <h1 className="text-2xl font-bold leading-[1.1] tracking-[-0.01em] text-foreground">
            Agent
          </h1>
        </div>

        <div className="flex flex-col items-center gap-3">
          <h2 className="text-3xl font-extrabold tracking-tight text-foreground">
            {status.title}
          </h2>
          <p className="text-base leading-relaxed text-muted-foreground">
            {status.message}
          </p>
        </div>

        {status.eta ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/30 px-4 py-1.5 text-sm font-medium text-foreground">
            <Clock className="size-3.5 text-muted-foreground" aria-hidden />
            <span>{status.eta}</span>
          </div>
        ) : null}

        {channelUrl ? (
          <a
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#18399b] hover:underline [[data-theme='dark']_&]:text-white"
          >
            Follow updates in our Teams channel
            <ExternalLink className="size-3.5" aria-hidden />
          </a>
        ) : null}

        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={tryAgain}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw
              className={refreshing ? 'size-4 animate-spin' : 'size-4'}
              aria-hidden
            />
            {refreshing ? 'Refreshing…' : 'Try again'}
          </Button>
          <Button
            variant="brand-outline"
            size="sm"
            onClick={() => navigate({ to: '/game' })}
            className="gap-2"
          >
            <Gamepad2 className="size-4" aria-hidden />
            Play game
          </Button>
        </div>
      </div>
    </div>
  );
}
